import { useState } from "react";
import CopyCode from "../../CopyCode/CopyCode";

const Timeline = () => {
  return (
    <div className="flex flex-col gap-y-10 p-6">
      {/* BASIC TIMELINES */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
        <h2 className="text-xl font-bold text-white mb-6">Timeline Events</h2>
        <div className="grid grid-cols-1 gap-4">
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Vertical Timeline</label>
            <div className="space-y-4 p-2">
              {["Project Started", "Design Phase", "Development", "Testing", "Deployed"].map((event, i) => (
                <div key={event} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`w-4 h-4 rounded-full ${i <= 2 ? "bg-amber-400" : "bg-slate-600"}`}></div>
                    {i < 4 && <div className="w-0.5 h-8 bg-slate-600 my-1"></div>}
                  </div>
                  <div>
                    <p className="text-sm text-slate-300 font-medium">{event}</p>
                    <p className="text-xs text-slate-500">2 days ago</p>
                  </div>
                </div>
              ))}
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="flex gap-3">
  <div className="flex flex-col items-center">
    <div className="w-4 h-4 rounded-full bg-amber-400"></div>
    <div className="w-0.5 h-8 bg-slate-600"></div>
  </div>
  <div>
    <p className="text-slate-300">Event</p>
    <p className="text-slate-500">2 days ago</p>
  </div>
</div>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Horizontal Timeline</label>
            <div className="flex items-center gap-2 p-3 overflow-x-auto">
              {["Jan", "Feb", "Mar", "Apr", "May", "Jun"].map((month, i) => (
                <div key={month} className="flex flex-col items-center flex-shrink-0">
                  <div className={`w-3 h-3 rounded-full ${i <= 2 ? "bg-amber-400" : "bg-slate-600"}`}></div>
                  <p className="text-xs text-slate-400 mt-1">{month}</p>
                </div>
              ))}
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="flex items-center gap-2">
  {months.map((m, i) => (
    <div key={m} className="flex flex-col items-center">
      <div className={i <= 2 ? "bg-amber-400" : "bg-slate-600"}></div>
      <p className="text-xs">{m}</p>
    </div>
  ))}
</div>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">With Descriptions</label>
            <div className="space-y-4">
              {[{title: "Launch", desc: "Product went live"}, {title: "Update", desc: "Major new features"}].map(item => (
                <div key={item.title} className="flex gap-3 p-2 border-l-4 border-amber-400">
                  <div className="w-4 h-4 rounded-full bg-amber-400 flex-shrink-0 mt-1"></div>
                  <div>
                    <p className="text-sm text-slate-200 font-medium">{item.title}</p>
                    <p className="text-xs text-slate-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="flex gap-3 p-2 border-l-4 border-amber-400">
  <div className="w-4 h-4 rounded-full bg-amber-400"></div>
  <div>
    <p className="text-slate-200">Title</p>
    <p className="text-slate-500">Description</p>
  </div>
</div>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Colored Timeline</label>
            <div className="space-y-3">
              {[{color: "cyan", label: "Started"}, {color: "purple", label: "In Progress"}, {color: "green", label: "Completed"}].map(item => (
                <div key={item.label} className="flex gap-2 items-center">
                  <div className={`w-3 h-3 rounded-full bg-${item.color}-500`}></div>
                  <span className="text-sm text-slate-300">{item.label}</span>
                </div>
              ))}
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`{items.map(item => (
  <div className="flex gap-2 items-center">
    <div className={\`bg-\${item.color}-500\`}></div>
    <span>{item.label}</span>
  </div>
))}`} />
          </div>
        </div>
      </div>

      {/* ADVANCED TIMELINES */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
        <h2 className="text-xl font-bold text-amber-400 mb-6">Advanced Variants</h2>
        <div className="grid grid-cols-1 gap-4">
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Alternating Timeline</label>
            <div className="space-y-4">
              {["Left Event", "Right Event", "Left Event"].map((event, i) => (
                <div key={`${event}-${i}`} className={`flex ${i % 2 === 0 ? "flex-row" : "flex-row-reverse"} items-center gap-3`}>
                  <div className="flex-1 text-right text-slate-400 text-xs">{i % 2 === 0 ? event : ""}</div>
                  <div className="w-4 h-4 rounded-full bg-amber-400 flex-shrink-0"></div>
                  <div className="flex-1 text-left text-slate-400 text-xs">{i % 2 !== 0 ? event : ""}</div>
                </div>
              ))}
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`{items.map((e, i) => (
  <div className={i % 2 === 0 ? "flex-row" : "flex-row-reverse"}>
    <div className="flex-1">{i % 2 === 0 ? e : ""}</div>
    <div className="w-4 h-4 bg-amber-400"></div>
    <div className="flex-1">{i % 2 !== 0 ? e : ""}</div>
  </div>
))}`} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Timeline;


